import React, {
  SyntheticEvent,
  useEffect,
  useState,
  useMemo,
  useCallback,
  Fragment,
} from 'react';
import Table, { ColumnConfig } from 'components/table';
import { POS_CONFIG } from 'state/posConfig/type';
import QUERY from 'utils/query';
import { useLocation, useNavigate } from 'react-router-dom';
import Pagination from 'components/pagination';
import useQuery from 'utils/hooks/useQuery';
import TableToolbar from './TableToolbar';
import useLastUpdatedTimer from './hooks/useLastUpdatedTimer';

const ITEMS_PER_PAGE = 25;

interface IProps {
  guestList: POS_CONFIG.IGuestListData[];
  isLoading: boolean;
  onGuestSelect?: (guestData: POS_CONFIG.IGuestListData) => void;
  onRefresh?: () => void;
}

const GuestListTab = ({
  guestList,
  isLoading,
  onGuestSelect,
  onRefresh,
}: IProps): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = useQuery(location.search);
  const [currentPage, setCurrentPage] = useState(1);
  const { lastUpdateMessage, resetTimer } = useLastUpdatedTimer(guestList);

  const searchString = urlParams.get('search')?.toLowerCase() || '';

  const filteredGuests = useMemo(() => {
    if (!guestList || guestList.length === 0) return [];

    return guestList.filter((guest) => {
      const matchesSearch = searchString
        ? guest?.firstName?.toLowerCase().includes(searchString) ||
          guest?.lastName?.toLowerCase().includes(searchString) ||
          guest?.phone?.toLowerCase().includes(searchString)
        : true;
      return matchesSearch;
    });
  }, [guestList, searchString]);

  const { totalPages, paginatedGuests } = useMemo(() => {
    const pages = Math.ceil(filteredGuests.length / ITEMS_PER_PAGE);
    const paginatedData = filteredGuests.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      currentPage * ITEMS_PER_PAGE
    );
    return { totalPages: pages, paginatedGuests: paginatedData };
  }, [filteredGuests, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchString]);

  const prevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  const nextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);

  const handleCheckoutClick = useCallback(
    (row: POS_CONFIG.IGuestListData, e: SyntheticEvent) => {
      e.stopPropagation();
      if (onGuestSelect) {
        onGuestSelect(row);
        navigate({
          search: QUERY.update(location.search, {
            tab: 'checkout',
          }),
        });
      }
    },
    [onGuestSelect, navigate, location.search]
  );

  const columns: ColumnConfig<POS_CONFIG.IGuestListData>[] = useMemo(
    () => [
      {
        key: 'firstName',
        label: 'First Name',
        renderCell: (row) => <>{row.firstName}</>,
      },
      {
        key: 'lastName',
        label: 'Last Name',
        renderCell: (row) => <>{row.lastName}</>,
      },
      {
        key: 'phone',
        label: 'Phone',
        renderCell: (row) => <>{row.phone}</>,
      },
      {
        key: 'message',
        label: 'Status',
        renderCell: (row) => <>{row.message}</>,
      },
      {
        key: 'checkoutAction',
        label: 'Actions',
        renderCell: (row) => (
          <span
            className="text-dark-green-link cursor-pointer"
            onClick={(e) => handleCheckoutClick(row, e)}
          >
            Checkout &rarr;
          </span>
        ),
      },
    ],
    [handleCheckoutClick]
  );

  const handleRowClick = useCallback(
    (row: POS_CONFIG.IGuestListData, _: SyntheticEvent) => {
      if (onGuestSelect) {
        onGuestSelect(row);
        navigate({
          search: QUERY.update(location.search, {
            tab: 'checkout',
          }),
        });
      }
    },
    [onGuestSelect, navigate, location.search]
  );

  const handleRefresh = useCallback(() => {
    onRefresh?.();
    resetTimer();
  }, [onRefresh, resetTimer]);

  return (
    <Fragment>
      <TableToolbar
        queryParams={location.search}
        onRefresh={onRefresh ? handleRefresh : undefined}
        lastUpdateMessage={lastUpdateMessage}
        refreshTestId="refresh-guest-list-button"
      />

      <div className="overflow-x-auto overflow-y-hidden relative w-full">
        <Table
          data={paginatedGuests}
          columns={columns}
          onRowClick={handleRowClick}
          isLoading={isLoading}
          isEmpty={filteredGuests.length === 0}
          customTheme={{
            Table: `
              min-width: 100% !important;
              grid-template-columns: repeat(4, minmax(150px, 1fr)) 120px !important;
            `,
            BaseCell: `
              &:nth-of-type(5) {
                position: sticky !important;
                right: 0px !important;
                background-color: white !important;
                z-index: 2 !important;
                box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1) !important;
                min-width: 120px !important;
                max-width: 120px !important;
              }
            `,
            HeaderCell: `
              &:nth-of-type(5) {
                position: sticky !important;
                right: 0px !important;
                background-color: white !important;
                z-index: 2 !important;
                box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1) !important;
                font-weight: 600 !important;
                min-width: 120px !important;
                max-width: 120px !important;
              }
            `,
          }}
        />
      </div>

      <Pagination
        pageId={location?.search}
        results={filteredGuests.length}
        prevPage={prevPage}
        nextPage={nextPage}
        prevKey={currentPage > 1 ? String(currentPage - 1) : null}
        nextKey={currentPage < totalPages ? String(currentPage + 1) : null}
      />
    </Fragment>
  );
};

export default GuestListTab;
